import { useEffect, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { CupSoda, X } from "lucide-react";
import { SIZES, FLAVORS, ADDON_CATEGORIES, formatBRL, slug } from "../data/menu";
import { Chapter } from "./Chapter";
import { AddonChip } from "./AddonChip";

export const CopoBuilder = ({ onAdd, hasItems }) => {
  const [sizeId, setSizeId] = useState("400");
  const [flavorId, setFlavorId] = useState("acai");
  const [addons, setAddons] = useState([]);
  const [builderInView, setBuilderInView] = useState(false);
  const rootRef = useRef(null);

  useEffect(() => {
    const el = rootRef.current;
    if (!el) return;
    const obs = new IntersectionObserver(([entry]) => setBuilderInView(entry.isIntersecting), {
      rootMargin: "-15% 0px -15% 0px",
    });
    obs.observe(el);
    return () => obs.disconnect();
  }, []);

  const size = SIZES.find((s) => s.id === sizeId);
  const flavor = FLAVORS.find((f) => f.id === flavorId);
  const addonsTotal = addons.reduce((acc, a) => acc + a.price, 0);
  const total = size.price + addonsTotal;

  const toggleAddon = (item) =>
    setAddons((prev) =>
      prev.some((a) => a.name === item.name)
        ? prev.filter((a) => a.name !== item.name)
        : [...prev, item]
    );

  const handleAdd = () => {
    onAdd({
      title: `Copo ${size.label} · ${flavor.label}`,
      details: addons.length ? addons.map((a) => a.name) : ["Sem adicionais"],
      unitPrice: total,
      qty: 1,
    });
    setAddons([]);
  };

  return (
    <div ref={rootRef}>
      <Chapter number="01" title="Monte seu copo" accent="#10B981">
        Escolha o tamanho, o sabor da base e capriche nos adicionais. O total
        aparece em tempo real, sem surpresa.
      </Chapter>

      <div className="grid gap-12 lg:grid-cols-[1fr_370px]">
        <div className="space-y-14">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h3 className="mb-5 font-grotesk text-xs font-bold uppercase tracking-[0.3em] text-purple-300/70">
              1 · Tamanho
            </h3>
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
              {SIZES.map((s) => {
                const active = s.id === sizeId;
                return (
                  <button
                    key={s.id}
                    type="button"
                    data-testid={`size-option-${s.id}`}
                    onClick={() => setSizeId(s.id)}
                    className={`relative rounded-3xl border px-5 py-6 text-left transition-all duration-300 hover:-translate-y-1 ${
                      active
                        ? "border-emerald-400 bg-emerald-400/10 shadow-[0_0_35px_-8px_rgba(16,185,129,0.5)]"
                        : "border-purple-500/25 bg-white/5 hover:border-purple-300/50"
                    }`}
                  >
                    {s.popular && (
                      <span className="absolute -top-2.5 left-4 rounded-full bg-amber-400 px-2.5 py-0.5 text-[10px] font-extrabold uppercase tracking-wider text-[#1E053B]">
                        Queridinho
                      </span>
                    )}
                    <span className="block font-syne text-xl font-extrabold text-white">
                      {s.label}
                    </span>
                    <span className="mt-1 block text-sm font-semibold text-emerald-400">
                      {formatBRL(s.price)}
                    </span>
                  </button>
                );
              })}
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h3 className="mb-5 font-grotesk text-xs font-bold uppercase tracking-[0.3em] text-purple-300/70">
              2 · Sabor da base
            </h3>
            <div className="flex flex-wrap gap-3">
              {FLAVORS.map((f) => {
                const active = f.id === flavorId;
                return (
                  <button
                    key={f.id}
                    type="button"
                    data-testid={`flavor-option-${f.id}`}
                    onClick={() => setFlavorId(f.id)}
                    className={`rounded-full border px-7 py-3.5 text-sm font-bold transition-all duration-300 ${
                      active
                        ? "border-transparent bg-white text-[#1E053B]"
                        : "border-purple-500/30 bg-white/5 text-purple-100 hover:border-purple-300/60"
                    }`}
                  >
                    {f.label}
                  </button>
                );
              })}
            </div>
          </motion.div>

          <div>
            <h3 className="mb-8 font-grotesk text-xs font-bold uppercase tracking-[0.3em] text-purple-300/70">
              3 · Adicionais — escolha quantos quiser
            </h3>
            <div className="space-y-10">
              {ADDON_CATEGORIES.map((cat, ci) => (
                <motion.div
                  key={cat.id}
                  initial={{ opacity: 0, y: 24 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-40px" }}
                  transition={{ duration: 0.55, delay: (ci % 3) * 0.08 }}
                >
                  <div className="mb-4 flex items-center gap-3">
                    <span
                      className="h-3 w-3 rounded-full"
                      style={{ backgroundColor: cat.color }}
                    />
                    <h4 className="font-syne text-lg font-bold text-white">{cat.name}</h4>
                    <span className="text-xs text-purple-300/50">
                      {cat.items.length} opções
                    </span>
                  </div>
                  <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                    {cat.items.map((item) => (
                      <AddonChip
                        key={item.name}
                        item={item}
                        color={cat.color}
                        active={addons.some((a) => a.name === item.name)}
                        testId={`addon-chip-${cat.id}-${slug(item.name)}`}
                        onToggle={() => toggleAddon(item)}
                      />
                    ))}
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </div>

        <aside className="lg:sticky lg:top-24 lg:self-start">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
            className="rounded-[2rem] border border-purple-500/25 bg-[#2D0B5A]/70 p-7 backdrop-blur-xl"
            data-testid="builder-summary"
          >
            <div className="flex items-center gap-3">
              <CupSoda className="h-5 w-5 text-amber-400" />
              <h3 className="font-syne text-lg font-extrabold text-white">Seu copo</h3>
            </div>

            <div className="mt-6 space-y-3 text-sm">
              <div className="flex justify-between text-purple-100">
                <span>
                  {size.label} · {flavor.label}
                </span>
                <span className="font-semibold">{formatBRL(size.price)}</span>
              </div>
              {addons.length > 0 && (
                <div className="max-h-44 space-y-2 overflow-y-auto pr-1">
                  {addons.map((a) => (
                    <div
                      key={a.name}
                      className="flex items-center justify-between gap-2 text-purple-200/80"
                    >
                      <span className="flex items-center gap-1.5">
                        <button
                          type="button"
                          aria-label={`Remover ${a.name}`}
                          data-testid={`builder-remove-${slug(a.name)}`}
                          onClick={() => toggleAddon(a)}
                          className="text-purple-400 transition-colors hover:text-red-400"
                        >
                          <X className="h-3.5 w-3.5" />
                        </button>
                        {a.name}
                      </span>
                      <span>{formatBRL(a.price)}</span>
                    </div>
                  ))}
                </div>
              )}
              {addons.length === 0 && (
                <p className="text-purple-300/50">Nenhum adicional ainda.</p>
              )}
            </div>

            <div className="mt-6 border-t border-purple-500/25 pt-5">
              <div className="flex items-baseline justify-between">
                <span className="font-grotesk text-xs font-bold uppercase tracking-[0.25em] text-purple-300/70">
                  Total
                </span>
                <span
                  data-testid="builder-total"
                  className="font-syne text-3xl font-extrabold text-emerald-400"
                >
                  {formatBRL(total)}
                </span>
              </div>
              <button
                type="button"
                data-testid="builder-add-button"
                onClick={handleAdd}
                className="mt-5 w-full rounded-full bg-emerald-400 py-4 font-grotesk text-sm font-bold uppercase tracking-[0.15em] text-[#1E053B] transition-transform duration-300 hover:scale-[1.03] active:scale-95"
              >
                Adicionar ao pedido
              </button>
            </div>
          </motion.div>
        </aside>
      </div>

      <AnimatePresence>
        {builderInView && (
          <motion.div
            initial={{ y: 80, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 80, opacity: 0 }}
            transition={{ type: "spring", stiffness: 300, damping: 28 }}
            className={`fixed inset-x-4 z-30 lg:hidden ${hasItems ? "bottom-24" : "bottom-5"}`}
          >
            <button
              type="button"
              data-testid="builder-add-button-mobile"
              onClick={handleAdd}
              className="flex w-full items-center justify-between gap-4 rounded-full border border-emerald-300/40 bg-[#2D0B5A]/95 px-6 py-4 shadow-[0_20px_50px_-15px_rgba(0,0,0,0.7)] backdrop-blur-xl"
            >
              <span className="font-grotesk text-xs font-bold uppercase tracking-[0.15em] text-purple-100">
                Adicionar copo ao pedido
              </span>
              <span className="font-syne text-lg font-extrabold text-emerald-400">
                {formatBRL(total)}
              </span>
            </button>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};
